// WCC Projects > Change of Routine
// src > ts > index.ts
// Created 22 February 2021, Updated 31 October 2024 (Version 3.0.5)

// MODULES

import * as app from "./app";
import firebase from "firebase/app";

// TYPES

import { ProcessStageState, FormType, ProcessStage, Form, DatabaseFormContent, Organisation } from './model';
type SortOptions = "date" | "form-title" | "submitter-name";
type SearchOptions = "none" | "form-title" | "form-content" | "submitter-name";

// CONSTANTS

const archiveEmailAddresses = ["pdaghero1@parra.catholic.edu.au", "mcauleystudentreception@parra.catholic.edu.au", "mcauleyteachersadmin@parra.catholic.edu.au"];

// VARIABLES

// let staff: UserData[] = [];
let organisationData: Organisation;
let editingForms: Form[] = [];
let viewingForms: Form[] = [];
let sortVariable: SortOptions = "date";
let sortDirection: ("default" | "reversed") = "default";
let searchVariable: SearchOptions = "form-content";
let pendingSearchTimeout: (NodeJS.Timeout | null) = null;

// FUNCTIONS

const main = () => {
  jQuery(setDocumentListeners);
  Promise.all([getOrganisationData(), loadUsersEditingForms(), loadUsersViewingForms(), getUser()]).then(promiseResults => {
    organisationData = promiseResults[0];
    editingForms = promiseResults[1];
    viewingForms = promiseResults[2];
    const user = promiseResults[3];
    editingForms.sort(formsSorter);
    layoutUserSpecificPermissions(user.email!);
    layoutForms(editingForms, $("#my-forms"), true);
    viewingForms.sort(formsSorter);
    layoutForms(viewingForms, $("#forms-to-approve"), false);
    $("#recent-forms .segmented-control").show();
    const waitingFormsCount = countFormsWaitingOnUser(viewingForms, user.email!);
    if (waitingFormsCount > 0) {
      $("#forms-to-approve-badge").text(waitingFormsCount);
      $("#forms-to-approve-badge").show();
    }
    $("#my-forms").show();
    $("#loading").hide();
  }).catch(error => {
    if (error === "No user signed in") {
      document.location.href = "./signin.html";
    }
    console.error(error);
  });
};

const setDocumentListeners = () => {
  $("#actions-container #new-form").on("click", showNewFormPopup);
  $("#actions-container #sort-forms").on("click", () => $("#sort-container").toggle());
  $("#actions-container #search-forms").on("click", showSearchForms);
  $("#sort-container li").on("click", sortOptionClicked);
  $("#search-container #search-field").on("keyup", prepareSearchForms);
  $("#search-container #cancel-search").on("click", hideSearchForms);
  $("#search-container #search-criteria").on("change", searchOptionClicked);
  $("#recent-forms .segmented-control li").on("click", segmentedControlClicked);
  $("#recent-forms").on("click", ".form .actions .edit", goToForm);
  $("#recent-forms").on("click", ".form .actions .duplicate", showDuplicateFormPopup);
  $("#recent-forms").on("click", ".form .actions .delete", showDeleteFormPopup);
  $("#recent-forms").on("click", ".form .actions .disabled-delete", showDisabledDeleteFormPopup);
  $(".popup").on("click", didClickOutPopup);
  $("#new-form-popup #form-type").on("change", formTypeDidChange);
  $("#new-form-popup #begin-new-form").on("click", beginNewForm);
  $("#duplicate-form-popup #duplicate-form").on("click", duplicateForm);
  $("#delete-form-popup #delete-form").on("click", deleteForm);
  $("#disabled-delete-form-popup .button").on("click", hideAllPopups);
};

const didClickOutPopup = (event: Event) => {
  if (event.target && $(event.target).closest(".popup-container").length === 0) {
    hideAllPopups();
  }
};

const hideAllPopups = () => {
  $(".popup").hide();
};

const segmentedControlClicked = (event: Event) => {
  const $li = $(event.currentTarget!);
  $li.siblings().removeClass("selected");
  $li.addClass("selected");
  if ($li.attr("id") === "my-forms-option") {
    $("#search-field").attr("placeholder", "Search my forms");
    $("#my-forms").show();
    $("#forms-to-approve").hide();
  } else {
    $("#search-field").attr("placeholder", "Search forms to approve");
    $("#my-forms").hide();
    $("#forms-to-approve").show();
  }
};

const showNewFormPopup = () => {
  $(".test-form").hide();
  $("#new-form-popup #form-type").val("");
  $("#new-form-popup #begin-new-form").addClass("disabled");
  $("#new-form-popup").show();
};

const formsSorter = (a: Form, b: Form): number => {
  let direction = sortDirection === "default" ? 1 : -1;
  if (sortVariable === "submitter-name") {
    const aName = nameForEmail(a.editors[0] ?? a.viewers[0]).toLowerCase();
    const bName = nameForEmail(b.editors[0] ?? b.viewers[0]).toLowerCase();
    if (aName !== bName) {
      return aName > bName ? direction : -direction;
    }
  }
  if (sortVariable === "submitter-name" || sortVariable === "form-title") {
    const aTitle = (a.data.titleOfMeeting || a.data.titleOfInservice || a.data.formTitle || "Untitled Form").toLowerCase();
    const bTitle = (b.data.titleOfMeeting || b.data.titleOfInservice || b.data.formTitle || "Untitled Form").toLowerCase();
    return aTitle > bTitle ? direction : -direction;
  }
  if (!a.submitDate && !a.editDate) {
    return -direction;
  }
  if (!b.submitDate && !b.editDate) {
    return direction;
  }
  let aDate = a.editDate ?? a.submitDate!;
  a.process.forEach(processStage => {
    if (processStage.date != null && processStage.date > aDate) {
      aDate = processStage.date;
    }
  });
  let bDate = b.editDate ?? b.submitDate!;
  b.process.forEach(processStage => {
    if (processStage.date != null && processStage.date > bDate) {
      bDate = processStage.date;
    }
  });
  return aDate < bDate ? direction : -direction;
};

const formsFilter = (form: Form): boolean => {
  const searchTerm = ($("#search-field").val() as string).toLowerCase();
  if (searchVariable === "submitter-name") {
    const submitterName = nameForEmail(form.editors[0] ?? form.viewers[0]).toLowerCase();
    return submitterName.includes(searchTerm);
  }
  const formTitle = form.data.titleOfMeeting || form.data.titleOfInservice || form.data.formTitle || "Untitled Form";
  if (formTitle.toLowerCase().includes(searchTerm)) {
    return true;
  }
  if (searchVariable === "form-content") {
    for (const field of Object.values(form.data)) {
      if (typeof field === "string" && field.toLowerCase().includes(searchTerm)) {
        return true;
      }
    }
  }
  return false;
};

const sortOptionClicked = (event: JQuery.ClickEvent<HTMLElement>) => {
  $("#sort-container li").removeClass("selected");
  $("#sort-container li").removeClass("reverse");
  const newSortVariable: SortOptions = event.currentTarget.id;
  $(`#sort-container li#${newSortVariable}`).addClass("selected");
  if (newSortVariable === sortVariable) {
    if (sortDirection === "default") {
      sortDirection = "reversed";
      $("#sort-container li.selected").addClass("reverse");
    } else {
      sortDirection = "default";
    }
  } else {
    sortDirection = "default";
  }
  sortVariable = newSortVariable;
  editingForms.sort(formsSorter);
  layoutForms(editingForms, $("#my-forms"), true);
  viewingForms.sort(formsSorter);
  layoutForms(viewingForms, $("#forms-to-approve"), false);
  $("#sort-container").hide();
};

const showSearchForms = () => {
  $("#search-field").val("");
  $("#header-container").addClass("search");
  $("#search-field").focus();
};

const hideSearchForms = () => {
  if (pendingSearchTimeout) {
    clearTimeout(pendingSearchTimeout);
  }
  pendingSearchTimeout = null;
  searchVariable = "none";
  $("#search-field").val("");
  $("#header-container").removeClass("search");
  layoutForms(editingForms, $("#my-forms"), true);
  layoutForms(viewingForms, $("#forms-to-approve"), false);
};

const searchOptionClicked = () => {
  searchVariable = $("#search-criteria").val() as SearchOptions;
  filterFormsForSearch();
};

const prepareSearchForms = () => {
  if (pendingSearchTimeout) {
    clearTimeout(pendingSearchTimeout);
  }
  pendingSearchTimeout = setTimeout(filterFormsForSearch, 350);
};

const filterFormsForSearch = () => {
  const filteredEditingForms = editingForms.filter(formsFilter);
  layoutForms(filteredEditingForms, $("#my-forms"), true);
  const filteredViewingForms = viewingForms.filter(formsFilter);
  layoutForms(filteredViewingForms, $("#forms-to-approve"), false);
};

const countFormsWaitingOnUser = (forms: Form[], email: string): number => {
  let count = 0;
  for (const form of forms) {
    if (isNextSigner(form, email)) {
      count ++;
    }
  }
  return count;
};

/**
 * Returns true if the provided email address is the next
 * process stage, has the state 'none' and all process stages
 * before it have the state 'approved'
 */
const isNextSigner = (form: Form, email: string): boolean => {
  for (const processStage of form.process) {
    if (processStage.email === email && processStage.state === ProcessStageState.None)
      return true; // User is next signer
    if (processStage.state !== ProcessStageState.Approved)
      return false; // Awaiting previous process stage
  }
  return false;
};

const beginNewForm = async () => {
  let formTypeString = $("#new-form-popup #form-type").val() as string;
  if (formTypeString != null) {
    const user = await getUser();
    if (user.email == null) {
      throw new Error("User does not have an associated email so cannot create forms");
    }
    showButtonLoading($("#new-form-popup #begin-new-form"));
    let isTestForm = false;
    if (formTypeString.includes("Test")) {
      formTypeString = formTypeString.slice(5);
      isTestForm = true;
    }
    let formData: any = null;
    const formType = formTypeString as keyof typeof FormType as FormType;
    if (formType === FormType.ProfessionalLearningApplication || formType === FormType.MeetingApplication || formType === FormType.LeaveApplication) {
      const userName = organisationData.usersData.find(organisationUser => organisationUser.email === user.email)?.fullName;
      if (userName) {
        formData = { name: userName };
      }
    }
    createNewForm(formType, formData, isTestForm).then(formId =>
      document.location.href = "./form.html?id=" + formId
    ).catch(error => console.error(error));
  }
};

const loadUsersEditingForms = async (): Promise<Form[]> => {
  const user = await getUser();
  const formsRef = app.database().collection("forms").where("editors", "array-contains", user.email);
  const querySnapshot = await formsRef?.get();
  return cleanForms(querySnapshot);
};

const loadUsersViewingForms = async (): Promise<Form[]> => {
  const user = await getUser();
  // if (user.email?.includes("agebran2")) {
  //   const formsRef = app.database().collection("forms");
  //   const querySnapshot = await formsRef?.get();
  //   $("#all-forms-badge").text(querySnapshot.size);
  //   $("#all-forms-option").show();
  //   return cleanForms(querySnapshot);
  // }
  const formsRef = app.database().collection("forms").where("viewers", "array-contains", user.email);
  if (archiveEmailAddresses.includes(user.email ?? "")) {
    $("#recent-forms .segmented-control #forms-to-approve-option").text("Form Archives");
  }
  const querySnapshot = await formsRef?.get();
  return cleanForms(querySnapshot);
};

// const getStaffList = async (): Promise<void> => {
//   return await new Promise((resolve, reject) => {
//     app.storage().ref("staff-list.js").getDownloadURL().then(url => {
//       var xhr = new XMLHttpRequest();
//       xhr.responseType = "blob";
//       xhr.onload = () => {
//         xhr.response.text().then((responseText: string) => {
//           staff = eval(responseText);
//           resolve();
//         });
//       };
//       xhr.open("GET", url);
//       xhr.send();
//     }).catch(error => {
//       reject(error);
//     });
//   });
// };

const getOrganisationData = async (): Promise<Organisation> => {
  const snapshot = await app.database().collection('organisations').doc('i7CJzk2FBN6sfax4TXs5').get();
  const data = snapshot.data();
  if (!snapshot.exists || !data) {
    throw new Error("Organisation doesn't exist");
  }
  return data as Organisation;
};

const nameForEmail = (email: string): string => {
  return organisationData.usersData.find(organisationUser => organisationUser.email === email)?.fullName ?? email.split("@")[0];
};

const cleanForms = (querySnapshot: firebase.firestore.QuerySnapshot<firebase.firestore.DocumentData>): Form[] => {
  let forms: Form[] = [];
  querySnapshot.forEach(doc => {
    const formContent = doc.data() as DatabaseFormContent;
    let form = {
      id: doc.id,
      viewers: formContent.viewers,
      editors: formContent.editors,
      type: formContent.type,
      editDate: formContent.editDate ? timestampToDate(formContent.editDate) : null,
      submitDate: formContent.submitDate ? timestampToDate(formContent.submitDate) : null,
      process: formContent.process,
      data: formContent.data,
      attachments: formContent.attachments,
      isTestForm: formContent.isTestForm,
      isSuspended: formContent.isSuspended,
    };
    form.process = form.process.map(processStage => {
      if (processStage.date) {
        processStage.date = timestampToDate(processStage.date);
      }
      return processStage;
    });
    forms.push(form);
  });
  return forms;
};

const timestampToDate = (timestamp: any): Date => {
  return (timestamp as unknown as firebase.firestore.Timestamp).toDate();
};

const shortDateString = (date: Date) => {
  const day = date.getDate().toString();
  const month = date.toLocaleString('default', { month: 'short' });
  const year = date.getFullYear().toString();
  return `${day} ${month} ${year}`;
};

const createNewForm = async (formType: FormType, formData?: any, isTestForm?: boolean): Promise<string> => {
  const user = await getUser();
  if (user.email == null) {
    throw new Error("User does not have an associated email so cannot create forms");
  }
  const newForm: DatabaseFormContent = generateNewForm(user.email, formType, formData, isTestForm);
  const doc = await app.database().collection("forms").add(newForm);
  return doc.id;
};

const generateNewForm = (email: string, formType: FormType, formData?: any, isTestForm?: boolean): DatabaseFormContent => {
  return {
    editors: [email],
    viewers: [],
    type: formType,
    editDate: new Date(),
    submitDate: null,
    process: [],
    data: formData ?? {},
    attachments: [],
    isTestForm: isTestForm ?? false,
    isSuspended: false,
  };
};

const getUser = async (): Promise<firebase.User> => {
  return await new Promise((resolve, reject) => {
    app.authentication().onAuthStateChanged(user => {
      if (user == null)
        reject("No user signed in");
      else
        resolve(user);
    });
  });
};

const layoutForms = async (forms: Form[], $formsContainer: JQuery<HTMLElement>, isMyForm: boolean) => {
  const user = await getUser();
  $formsContainer.html("");
  for (const form of forms) {
    let nextSignerClass = "";
    if (isNextSigner(form, user.email!)) {
      nextSignerClass = " is-next-signer";
    }
    let summaryHtml = "";
    if (form.submitDate == null) {
      summaryHtml = "Not Submitted";
      if (form.editDate) {
        summaryHtml = `Edited on ${shortDateString(form.editDate)}`;
      }
    } else if (allProcessStagesApproved(form.process)) {
      summaryHtml = /* html */`<span class="approved">Approved</span>`;
      if (form.process[form.process.length - 1] && form.process[form.process.length - 1].date) {
        summaryHtml += ` on ${shortDateString(form.process[form.process.length - 1].date!)}`;
      }
    } else if (anyProcessStagesCancelled(form.process)) {
      const date = form.process.find(processStage => processStage.state === ProcessStageState.Cancelled)!.date!;
      summaryHtml = /* html */`<span class="cancelled">Cancelled</span> on ${shortDateString(date)}`;
    } else if (anyProcessStagesRejected(form.process)) {
      const date = form.process.find(processStage => processStage.state === ProcessStageState.Rejected)!.date!;
      summaryHtml = /* html */`<span class="rejected">Rejected</span> on ${shortDateString(date)}`;
    } else {
      summaryHtml = /* html */`<span class="submitted">Submitted</span> on ${shortDateString(form.submitDate)}`;
    }
    if (form.submitDate) {
      summaryHtml += ` ${generateProcessSnapshotImage(form.process)}`;
    }
    const formTitle = form.data.titleOfMeeting || form.data.titleOfInservice || form.data.formTitle || "Untitled Form";
    let formTypeString = form.type as string;
    if (!isMyForm && (form.type === FormType.ExcursionOrActivityApplication || form.type === FormType.ProfessionalLearningApplication)) {
      formTypeString = formTypeString.substring(0, formTypeString.length - 12);
    }
    $formsContainer.append(/* html */`
      <div id="${form.id}" class="form${nextSignerClass}">
        <div class="content">
          <div class="title">${formTitle}</div>
          <div class="type">${formTypeString} ${isMyForm ? "" : `by ${nameForEmail(form.editors[0] ?? form.viewers[0])}`}</div>
          <div class="info">${summaryHtml}</div>
        </div>
        <div class="actions">
          <div class="action edit">
            <div>
              <img src="./images/edit-icon.svg">
              <span>${isMyForm && form.submitDate == null ? "Edit" : "Review"}</span>
            </div>
          </div>
          <div class="action duplicate">
            <div>
              <img src="./images/duplicate-icon.svg">
              <span>Duplicate</span>
            </div>
          </div>
          ${form.submitDate == null ? /* html */`
            <div class="action delete">
              <div>
                <img src="./images/delete-icon.svg">
                <span>Delete</span>
              </div>
            </div>
          ` : /* html */`
            <div class="action disabled-delete">
              <div>
                <img src="./images/disabled-delete-icon.svg">
                <span>Delete</span>
              </div>
            </div>
          `}
        </div>
      </div>
  `);
  }
};

const layoutUserSpecificPermissions = (email: string) => {
  if (email.includes("agebran2")) {
    $("#form-type").append(/* html */`
      <option value="Test Professional Learning Application">[TEST FORM] Professional Learning Application</option>
      <option value="Test Meeting Application">[TEST FORM] Meeting Application</option>
      <option value="Test Excursion/In School Activity Application">[TEST FORM] Excursion/In School Activity Application</option>
      <option value="Test Leave Application">[TEST FORM] Leave Application</option>
      <option value="Excursion Application">Excursion Application [TEST]</option>
      <option value="Incursion Application">Incursion Application [TEST]</option>
    `);
  }
  if (email.includes("agebran2") || email.includes("jzhou2") || email.includes("akaia") || email.includes("mrefalo4") || email.includes("dgrigson")) {
    $("#organisation-settings").show();
  }
};

const formTypeDidChange = () => {
  const formType = $("#form-type").val() as string
  $(".test-form").toggle(formType.includes("Test"));
  if (formType) {
    $("#new-form-popup #begin-new-form").removeClass("disabled");
  }
};

const goToForm = (event: JQuery.ClickEvent<HTMLElement>) => {
  if (!event.currentTarget)
    return;
  const formId = event.currentTarget.parentElement.parentElement.id;
  document.location.href = "./form.html?id=" + formId;
};

const showDuplicateFormPopup = (event: JQuery.ClickEvent<HTMLElement>) => {
  if (!event.currentTarget) {
    return;
  }
  const formId = event.currentTarget.parentElement.parentElement.id;
  $("#duplicate-form-popup .popup-content #duplicate-form-preview").html($(`#forms-container #${formId}`).html());
  $("#duplicate-form-popup .popup-content #duplicate-form-preview .actions").html("");
  $("#duplicate-form-popup .popup-container").attr("id", formId);
  $("#duplicate-form-popup").show();
};

const duplicateForm = () => {
  const formId = $("#duplicate-form-popup .popup-container").attr("id");
  if (!formId) {
    return;
  }
  showButtonLoading($("#duplicate-form-popup #duplicate-form"));
  app.database().collection("forms").doc(formId).get().then(snapshot => {
    const form = snapshot.data() as DatabaseFormContent;
    if (!form) {
      return;
    }
    if (form.data.titleOfInservice) {
      form.data.titleOfInservice = `Copy of ${form.data.titleOfInservice}`;
    }
    if (form.data.titleOfMeeting) {
      form.data.titleOfMeeting = `Copy of ${form.data.titleOfMeeting}`;
    }
    if (form.data.formTitle) {
      form.data.formTitle = `Copy of ${form.data.formTitle}`;
    }
    createNewForm(form.type, form.data).then(formId => {
      document.location.href = "./form.html?id=" + formId;
    }).catch(error => {
      console.error(error);
    });
  });
};

const showDeleteFormPopup = (event: JQuery.ClickEvent<HTMLElement>) => {
  if (!event.currentTarget) {
    return;
  }
  const formId = event.currentTarget.parentElement.parentElement.id;
  $("#delete-form-popup .popup-content #delete-form-preview").html($(`#forms-container #${formId}`).html());
  $("#delete-form-popup .popup-content #delete-form-preview .actions").html("");
  $("#delete-form-popup .popup-container").attr("id", formId);
  $("#delete-form-popup").show();
};

const showDisabledDeleteFormPopup = (event: JQuery.ClickEvent<HTMLElement>) => {
  if (!event.currentTarget) {
    return;
  }
  $("#disabled-delete-form-popup").show();
};

const deleteForm = () => {
  const formId = $("#delete-form-popup .popup-container").attr("id");
  if (!formId) {
    return;
  }
  const $button = $("#delete-form-popup #delete-form");
  showButtonLoading($button);
  app.database().collection("forms").doc(formId).delete().then(() => {
    hideButtonLoading($button);
    $(`#forms-container #${formId}`).hide();
    editingForms = editingForms.filter(form => form.id !== formId);
    viewingForms = viewingForms.filter(form => form.id !== formId);
    $("#delete-form-popup").hide();
  });
};

const allProcessStagesApproved = (process: ProcessStage[]): boolean => {
  for (const processStage of process) {
    if (processStage.state !== ProcessStageState.Approved)
      return false;
  }
  return true;
};

const anyProcessStagesRejected = (process: ProcessStage[]): boolean => {
  for (const processStage of process) {
    if (processStage.state === ProcessStageState.Rejected) {
      return true;
    }
  }
  return false;
};

const anyProcessStagesCancelled = (process: ProcessStage[]): boolean => {
  for (const processStage of process) {
    if (processStage.state === ProcessStageState.Cancelled) {
      return true;
    }
  }
  return false;
};

const showButtonLoading = ($button: JQuery<HTMLElement>) => {
  $button.find(".button-text").hide();
  $button.find(".button-loading").show();
};

const hideButtonLoading = ($button: JQuery<HTMLElement>) => {
  $button.find(".button-text").show();
  $button.find(".button-loading").hide();
};

const generateProcessSnapshotImage = (process: ProcessStage[]) => {
  let processSnapshotHtml = "";
  for (let index = process.length - 1; index >= 0; index --) {
    const processStage = process[index];
    let processStageHtml = "";
    if (processStage.state === ProcessStageState.None) {
      processStageHtml = /* html */`
        <g transform="translate(0, 3)">
          <circle stroke="#FFFFFF" fill="#EBEBEB" cx="11" cy="11" r="11.75"></circle>
          <path fill="#808080" d="M16,11 C16,11.4142136 15.6642136,11.75 15.25,11.75 L6.75,11.75 C6.33578644,11.75 6,11.4142136 6,11 C6,10.5857864 6.33578644,10.25 6.75,10.25 L15.25,10.25 C15.6642136,10.25 16,10.5857864 16,11 Z"></path>
        </g>
      `;
    } else if (processStage.state === ProcessStageState.Rejected || processStage.state === ProcessStageState.Cancelled) {
      processStageHtml = /* html */`
        <g transform="translate(0, 3)">
          <circle stroke="#FFFFFF" fill="#${processStage.state === ProcessStageState.Rejected ? "F04343" : "F68531" }" cx="11" cy="11" r="11.75"></circle>
          <path fill="#FFFFFF" d="M8.52512627,7.46446609 L10.9997962,9.93879618 L13.4748737,7.46446609 C13.767767,7.17157288 14.2426407,7.17157288 14.5355339,7.46446609 C14.8284271,7.75735931 14.8284271,8.23223305 14.5355339,8.52512627 L12.0597962,10.9997962 L14.5355339,13.4748737 C14.8284271,13.767767 14.8284271,14.2426407 14.5355339,14.5355339 C14.2426407,14.8284271 13.767767,14.8284271 13.4748737,14.5355339 L10.9997962,12.0597962 L8.52512627,14.5355339 C8.23223305,14.8284271 7.75735931,14.8284271 7.46446609,14.5355339 C7.17157288,14.2426407 7.17157288,13.767767 7.46446609,13.4748737 L9.93879618,10.9997962 L7.46446609,8.52512627 C7.17157288,8.23223305 7.17157288,7.75735931 7.46446609,7.46446609 C7.75735931,7.17157288 8.23223305,7.17157288 8.52512627,7.46446609 Z"></path>
        </g>
      `;
    } else {
      processStageHtml = /* html */`
        <g transform="translate(0, 3)">
          <circle stroke="#FFFFFF" fill="#52D146" cx="11" cy="11" r="11.75"></circle>
          <path fill="#FFFFFF" d="M16.0333784,8.05545635 C16.3262716,8.34834957 16.3262716,8.8232233 16.0333784,9.11611652 L10.0229708,15.1265242 L10.0229708,15.1265242 C9.73007755,15.4194174 9.25520382,15.4194174 8.9623106,15.1265242 L5.78033009,11.9445436 C5.48743687,11.6516504 5.48743687,11.1767767 5.78033009,10.8838835 C6.0732233,10.5909903 6.54809704,10.5909903 6.84099026,10.8838835 L9.49264069,13.5341197 L14.9727182,8.05545635 C15.2656115,7.76256313 15.7404852,7.76256313 16.0333784,8.05545635 Z"></path>
        </g>
      `;
    }
    if (processStage.comment) {
      processStageHtml += /* html */`
        <g transform="translate(13, 0)">
          <circle stroke="#FFFFFF" fill="#00AFD7" cx="6" cy="6" r="6.75"></circle>
          <path fill="#FFFFFF" fill-rule="nonzero" d="M4.34773438,9.77441406 C4.4138151,9.77441406 4.47476888,9.75789388 4.5305957,9.72485352 C4.58642253,9.69181315 4.65421224,9.63883464 4.73396484,9.56591797 L5.91658203,8.49609375 L8.06648438,8.49609375 C8.38777344,8.4983724 8.66007161,8.44083659 8.88337891,8.32348633 C9.1066862,8.20613607 9.27644531,8.03523763 9.39265625,7.81079102 C9.50886719,7.5863444 9.56697266,7.31461589 9.56697266,6.99560547 L9.56697266,4.50390625 C9.56697266,4.18489583 9.50886719,3.91373698 9.39265625,3.69042969 C9.27644531,3.4671224 9.1066862,3.29622396 8.88337891,3.17773438 C8.66007161,3.05924479 8.38777344,3 8.06648438,3 L3.92048828,3 C3.59919922,3 3.32690104,3.05867513 3.10359375,3.17602539 C2.88028646,3.29337565 2.71052734,3.46427409 2.59431641,3.6887207 C2.47810547,3.91316732 2.42,4.18489583 2.42,4.50390625 L2.42,6.99560547 C2.42,7.31461589 2.47867513,7.58577474 2.59602539,7.80908203 C2.71337565,8.03238932 2.88313477,8.2027181 3.10530273,8.32006836 C3.3274707,8.43741862 3.59578125,8.49609375 3.91023438,8.49609375 L4.05037109,8.49609375 L4.05037109,9.43261719 C4.05037109,9.5374349 4.07657552,9.62060547 4.12898438,9.68212891 C4.18139323,9.74365234 4.2543099,9.77441406 4.34773438,9.77441406 Z M4.279375,4.84912109 C4.2246875,4.84912109 4.17797526,4.82918294 4.13923828,4.78930664 C4.1005013,4.74943034 4.08113281,4.70100911 4.08113281,4.64404297 C4.08113281,4.58935547 4.1005013,4.54264323 4.13923828,4.50390625 C4.17797526,4.46516927 4.2246875,4.44580078 4.279375,4.44580078 L7.67341797,4.44580078 C7.72810547,4.44580078 7.77538737,4.46516927 7.81526367,4.50390625 C7.85513997,4.54264323 7.87507813,4.58935547 7.87507813,4.64404297 C7.87507813,4.70100911 7.85513997,4.74943034 7.81526367,4.78930664 C7.77538737,4.82918294 7.72810547,4.84912109 7.67341797,4.84912109 L4.279375,4.84912109 Z M4.279375,5.92578125 C4.2246875,5.92578125 4.17797526,5.90641276 4.13923828,5.86767578 C4.1005013,5.8289388 4.08113281,5.78108724 4.08113281,5.72412109 C4.08113281,5.66943359 4.1005013,5.62158203 4.13923828,5.58056641 C4.17797526,5.53955078 4.2246875,5.51904297 4.279375,5.51904297 L7.67341797,5.51904297 C7.72810547,5.51904297 7.77538737,5.53955078 7.81526367,5.58056641 C7.85513997,5.62158203 7.87507813,5.66943359 7.87507813,5.72412109 C7.87507813,5.78108724 7.85513997,5.8289388 7.81526367,5.86767578 C7.77538737,5.90641276 7.72810547,5.92578125 7.67341797,5.92578125 L4.279375,5.92578125 Z M4.279375,7.00244141 C4.2246875,7.00244141 4.17797526,6.98250326 4.13923828,6.94262695 C4.1005013,6.90275065 4.08113281,6.85546875 4.08113281,6.80078125 C4.08113281,6.7438151 4.1005013,6.69596354 4.13923828,6.65722656 C4.17797526,6.61848958 4.2246875,6.59912109 4.279375,6.59912109 L6.48738281,6.59912109 C6.54434896,6.59912109 6.59277018,6.61848958 6.63264648,6.65722656 C6.67252279,6.69596354 6.69246094,6.7438151 6.69246094,6.80078125 C6.69246094,6.85546875 6.67252279,6.90275065 6.63264648,6.94262695 C6.59277018,6.98250326 6.54434896,7.00244141 6.48738281,7.00244141 L4.279375,7.00244141 Z"></path>
        </g>
      `;
    }
    processSnapshotHtml += /* html */`
      <g transform="translate(${20.5 + 19 * index}, 1.5)">
        ${processStageHtml}
      </g>
    `;
  }
  return /* html */`
    <svg height="28px" viewBox="0 0 ${28 + 19 * process.length} 28">
      <g stroke="none" stroke-width="1.5" fill="none" fill-rule="evenodd">
        ${processSnapshotHtml}
        <g transform="translate(1.5, 1.5)">
          <g transform="translate(0, 3)">
            <circle stroke="#FFFFFF" fill="#00AFD7" cx="11" cy="11" r="11.75"></circle>
            <path fill="#FFFFFF" d="M16.0333784,8.05545635 C16.3262716,8.34834957 16.3262716,8.8232233 16.0333784,9.11611652 L10.0229708,15.1265242 L10.0229708,15.1265242 C9.73007755,15.4194174 9.25520382,15.4194174 8.9623106,15.1265242 L5.78033009,11.9445436 C5.48743687,11.6516504 5.48743687,11.1767767 5.78033009,10.8838835 C6.0732233,10.5909903 6.54809704,10.5909903 6.84099026,10.8838835 L9.49264069,13.5341197 L14.9727182,8.05545635 C15.2656115,7.76256313 15.7404852,7.76256313 16.0333784,8.05545635 Z"></path>
          </g>
        </g>
      </g>
    </svg>
  `;
};

// INITIAL SCRIPTS

main();
